import { DeliverableType } from '@atlas/types'
import { SectionCard } from '@atlas/ui-components'
import { formatBytes } from '@atlas/ui-domain'
import { DialogContent, DialogTitle } from '@atlas/ui-primitives'
import { CalendarDays } from 'lucide-react'
import { type StepFooterCta, WorkOrderStepFooter } from '@/components/work-orders/WorkOrderStepFooter'
import type { WorkOrder } from '@/domain/work-order.types'
import { useDesignFiles } from '@/hooks/useDesignFiles'
import { deliverableOwner, downloadFile, useDeleteWorkOrderFile, useUploadDesignFile } from '@/hooks/useWorkOrderFiles'
import { formatDeadline } from '@/lib/format-date'
import { WorkOrderCommentThread } from '../WorkOrders/WorkOrderCommentThread'
import { useSlotUploadDriver } from '../WorkOrders/workspace/sections/useSlotUploadDriver'
import { CappedUploadDropzone } from './CappedUploadDropzone'
import { DESIGN_TILE_GRID, DesignFileTile } from './DesignFileTile'
import { DraftRuleChecks } from './DraftRuleChecks'
import { useDraftRuleValidation } from './useDraftRuleValidation'

export interface DesignSubmitPanelProps {
	workOrder: WorkOrder
	isOpen: boolean
	onClose: () => void
	/** The move to Internal review; absent when the workflow offers none. */
	submit: StepFooterCta | null
}

/** The order's title, code and due date, as the panel opens on them. */
function PanelHeader({ workOrder: wo }: { workOrder: WorkOrder }) {
	return (
		<header className="flex items-start justify-between gap-4">
			<div className="min-w-0">
				<DialogTitle className="truncate text-ds-2xl">{wo.title}</DialogTitle>
				<p className="text-ds-sm text-ds-text-muted">{wo.orderCode}</p>
			</div>
			{wo.dueAt ? (
				<span className="inline-flex shrink-0 items-center gap-1.5 rounded-ds-md bg-ds-surface px-3 py-1.5 text-ds-sm font-medium text-ds-primary">
					<CalendarDays className="h-4 w-4" aria-hidden />
					<span aria-label="Due">{formatDeadline(wo.dueAt)}</span>
				</span>
			) : null}
		</header>
	)
}

/**
 * The designer's hand-over, over the right half of the page: upload this round's drafts,
 * validate them against the order's rules, leave the PM a note, and submit for validation.
 * The earlier rounds' files stay on the page behind it.
 */
export function DesignSubmitPanel({ workOrder: wo, isOpen, onClose, submit }: DesignSubmitPanelProps) {
	const { drafts } = useDesignFiles(wo.orderCode)
	const upload = useUploadDesignFile(wo.workOrderId, wo.orderCode, wo.tuple)
	const del = useDeleteWorkOrderFile(wo.orderCode)
	const validation = useDraftRuleValidation(drafts)
	const { rows, cardProps, uploadAll } = useSlotUploadDriver(
		deliverableOwner(wo.orderCode, DeliverableType.FINAL_VISUAL),
		upload.mutateAsync,
		drafts,
	)
	const sizeOf = (fileId: string | null) => drafts.find((d) => d.fileId === fileId)?.fileSize ?? null

	const remove = (fileId: string) => {
		validation.hideResults()
		del.mutate(fileId)
	}

	return (
		<DialogContent
			size="full"
			isOpen={isOpen}
			isDismissable
			onOpenChange={(open) => (open ? undefined : onClose())}
			overlayClassName="items-stretch justify-end p-0"
			className="flex h-full w-screen flex-col rounded-none bg-ds-background p-0 md:w-[50vw]"
		>
			<div className="flex-1 space-y-4 overflow-y-auto p-6">
				<PanelHeader workOrder={wo} />
				<SectionCard title="Submit design for review">
					<div className="space-y-3">
						<CappedUploadDropzone label="Upload design files" onFiles={(list) => void uploadAll(list)} />
						{rows.length ? (
							<ul className={DESIGN_TILE_GRID}>
								{rows.map((row) => {
									const card = cardProps(row)
									const stored = row.fileId && !row.upload ? row.fileId : null
									return (
										<DesignFileTile
											key={row.key}
											name={card.name}
											contentType={card.contentType}
											thumbnailUrl={card.thumbnailUrl}
											file={card.file}
											detail={stored ? formatBytes(sizeOf(stored)) : undefined}
											progress={row.upload && !row.upload.rejectedReason ? row.upload.progress : undefined}
											error={row.upload?.rejectedReason}
											onDownload={stored ? () => void downloadFile(stored) : undefined}
											onDelete={stored ? () => remove(stored) : undefined}
											onDismiss={row.upload?.rejectedReason ? card.onDismiss : undefined}
										/>
									)
								})}
							</ul>
						) : null}
					</div>
				</SectionCard>
				{drafts.length ? (
					<DraftRuleChecks
						results={validation.results}
						isRunning={validation.isRunning}
						draftCount={drafts.length}
						onValidate={validation.validate}
					/>
				) : null}
				<SectionCard title="Add a note for the PM">
					<WorkOrderCommentThread workOrder={wo} />
				</SectionCard>
			</div>
			<WorkOrderStepFooter onBack={onClose} backLabel="Close" cta={submit} className="static mx-0 mt-0" />
		</DialogContent>
	)
}
